import { makeStyles, Button as MaterialButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import cn from 'classnames';
import { useContext, useMemo } from 'react';
import { useMyMentorsContext } from '../../contexts/my-mentors-context';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { RouterContext } from '../../contexts/router-context';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import { TestId } from '../Testing/TestId';
import { Select, Text, Link } from '../common';
import List from '../common/list';
import MentorCard from '../list-cards/mentor-card';
import MyMentorsRowActions from '../mentors/my-mentors-row-actions';

const useStyles = makeStyles(() => ({
  centerBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    padding: 30,
    backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_45,
    textAlign: 'center',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 10,
    boxSizing: 'border-box',
  },
  filterSelect: {
    width: '100%',
  },
  emptyStateBtn: {
    marginTop: 16,
  },
}));

export default function MentorsList() {
  const classes = useStyles();
  const { rb } = useResourceBundles();
  const { prevPath } = useContext(RouterContext);
  const { hasAccessToAction } = useContext(UserContext);
  const {
    mentors,
    mentorsLogos,
    loadError,
    isLoading,
    selectedVenture,
    ventureOptions,
    isVenturesLoading,
    isEmptyState,
    setSelectedVenture,
  } = useMyMentorsContext();

  const prevMentorId = useMemo(() => {
    if (prevPath?.includes?.('/mentors/')) {
      const withoutQuery = prevPath.split('?')[0];
      const withoutHash = withoutQuery.split('#')[0];
      const splitPath = withoutHash.split('/');
      const mentorsIndex = splitPath.indexOf('mentors');
      const mentorId = splitPath[mentorsIndex + 1];

      if (mentorId && mentorId.length === 36) {
        return `card-${mentorId}`;
      }
    }
  }, [prevPath]);

  return (
    <List
      prevId={prevMentorId}
      loadError={loadError}
      isLoading={isLoading}
      filter={
        <TestId testId={'founder-my-mentors-table-ventures-dropdown'}>
          <Select
            className={cn(classes.filterSelect, CLASS_TRACKING.FILTER_ELEMENT)}
            value={selectedVenture}
            options={ventureOptions}
            onSelect={(item) => item && setSelectedVenture(item.value)}
            isDisabled={isVenturesLoading || !ventureOptions.length}
          />
        </TestId>
      }>
      {!ventureOptions.length && !isVenturesLoading ? (
        <div className={classes.centerBlock}>
          <Text variant='normal' testid='founder-my-mentor-table-no-ventures'>
            There are currently no active {rb('mentors')} assigned to you as you
            are not assigned to a venture. It could be simply a work in
            progress. If this is unexpected or lasts longer than necessary,
            please reach out to your Program Administrator to clarify.
          </Text>
        </div>
      ) : isEmptyState ? (
        <div className={classes.centerBlock}>
          <Text variant='normal' testid='founder-my-mentor-table-no-items'>
            Currently, your venture does not have any assigned {rb('mentors')}.
            Pease don't hesitate to contact your Program Administrator to find
            out more about new {rb('mentor')} assignments to your team.
          </Text>
          {hasAccessToAction('mentor.create') && (
            <MaterialButton
              component={Link}
              to={Pages.NEW_MENTOR}
              className={classes.emptyStateBtn}
              startIcon={<AddIcon />}
              variant='contained'
              color='primary'>
              {rb('mentor-u')}
            </MaterialButton>
          )}
        </div>
      ) : (
        <div className={classes.listContainer} key={selectedVenture}>
          {mentors.map((mentor) => (
            <MentorCard
              key={mentor.id}
              mentor={mentor}
              logo={mentorsLogos?.[mentor.id]}
              cardId={`card-${mentor.id}`}
              pathname={getRoutePath(Pages.MY_MENTOR_DETAILS, {
                mentorId: mentor.id,
                ventureId: selectedVenture,
              })}
              actions={<MyMentorsRowActions mentor={mentor} variant='list' />}
            />
          ))}
        </div>
      )}
    </List>
  );
}
